if (!customElements.get('snap-slider')) {
    class SnapSlider extends HTMLElement {
      constructor() {
        super();
      }
  
      connectedCallback() {
        this.slider = this.querySelector('._feed');
        this.previousButton = document.querySelector(
          `button[name="previous"][data-slider-target="${this.dataset.id}"]`
        );
        this.nextButton = document.querySelector(`button[name="next"][data-slider-target="${this.dataset.id}"]`);
  
        if (this.dataset.dummy === 'true') {
          const mutationOptions = {
            childList: true,
          };
          this.sliderObserver = new MutationObserver(this.initSlider.bind(this));
          this.sliderObserver.observe(this.slider, mutationOptions);
        }
  
        this.sliderBar = document.querySelector(`[data-slider-target="${this.dataset.id}"]._sliderBar`);
  
        /* INITIALIZATION CHECKERS */
        // if the slider is not visible, don't initialize
        if (!this.slider.clientWidth > 0) return;
  
        // contents css fix
        this.sliderItems = this.querySelector('._feed > *').classList.contains('contents')
          ? this.querySelectorAll('._feed .contents > *')
          : this.querySelectorAll('._feed > *');
  
        // if there are no slider items, don't initialize
        if (!this.sliderItems) {
          return;
        }
        if (!this.sliderItems.length === 0) return;
  
        /* VIP ELEMENTS SETTERS */
        // set the first element to the first element that has a width
        this.firstEl = [...this.sliderItems].find((item) => item.clientWidth > 0);
        // if the slider doesn't have an item with a width, don't initialize
        if (!this.firstEl) return;
  
        this.secondEl = [...this.sliderItems].find((item) => item.clientWidth > 0 && item !== this.firstEl);
  
        // set the last element to the last element that has a width
        this.lastEl = [...this.sliderItems].reverse().find((item) => item.clientWidth > 0);
        this.secondFromLastEl = [...this.sliderItems].reverse().find((item) => item.clientWidth > 0 && item !== this.lastEl);
  
  
        // slider init on intersection observer
        this.sliderIntersectionObserver = new IntersectionObserver(this.initSliderIfIntersecting.bind(this));
        this.sliderIntersectionObserver.observe(this.slider);
      }
  
      initSliderIfIntersecting(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting === true) {
            this.initSlider();
            this.sliderIntersectionObserver.unobserve(this.slider);
          }
        });
      }
  
      initSlider() {
        /* LISTENERS */
        // add scroll listeners
        this.slider.addEventListener('scroll', this.onScroll.bind(this));
  
        // add button listeners
        this.previousButton?.addEventListener('click', this.onButtonClick.bind(this));
        this.nextButton?.addEventListener('click', this.onButtonClick.bind(this));
        this.slider.addEventListener('scrollend', this.onScrollEnd.bind(this));
  
        if (this.dataset.carousel === 'true') {
          this.onCarouselMode();
          this.slider.scrollTo({
            left: this.firstEl.clientWidth * 2,
            behavior: 'instant',
          });
          // if the slider is in carousel mode, don't initialize observers
          return;
        }
  
        /* OBSERVERS */
        if (this.isScrolledToStart) {
          this.isScrolledToStart?.disconnect();
          this.isScrolledToEnd?.disconnect();
        }
  
        let options = { threshold: 1, root: this.slider };
  
        this.isScrolledToStart = new IntersectionObserver(this.togglePrevious.bind(this), options);
  
        this.sliderElementIntersection = new IntersectionObserver(this.toggleObservers.bind(this), { threshold: 1 });
  
        this.sliderElementIntersection.observe(this.slider);
  
        this.isScrolledToEnd = new IntersectionObserver(this.toggleNext.bind(this), options);
      }
  
      onScroll(event) {
        if (this.sliderBar) {
          if (!this.barThreshold) {
            this.barThreshold = this.sliderBar.style.transform.split('(')[1].split(')')[0];
          }
  
          let scale = event.target.scrollLeft / (event.target.scrollWidth - event.target.clientWidth);
          if (scale < this.barThreshold) {
            scale = this.barThreshold;
          }
  
          this.sliderBar.style.transform = `scaleX(${scale})`;
        }
      }
  
      onButtonClick(event) {
        event.preventDefault();
        const sliderStep = this.dataset.sliderStepBy === 'page' ? this.clientWidth : this.firstEl.clientWidth;
        const slideScrollPosition =
          event.currentTarget.name === 'next'
            ? this.slider.scrollLeft + sliderStep
            : this.slider.scrollLeft - sliderStep;
        this.slider.scrollTo({
          left: slideScrollPosition,
          behavior: 'smooth',
        });
        // when scroll ends
      }
  
      onScrollEnd(event) {
        let activeIndex;
        for (let i = 0; i < this.sliderItems.length; i++) {
          if (this.isItemVisibleInViewport(this.sliderItems[i])) {
            activeIndex = this.sliderItems[i].dataset.index;
            break;
          }
        }
        this.indexOutput = document.querySelector(`._sliderIndex[data-slider-target="${this.dataset.id}"]`);
        if (!this.indexOutput) return;
        this.indexOutput.textContent = `${activeIndex}/${this.sliderItems.length}`;
      }
  
      isItemVisibleInViewport(el) {
        const rect = el.getBoundingClientRect();
        return rect.left >= 0 && rect.right <= window.innerWidth;
      }
  
      toggleObservers(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting === true) {
            this.isScrolledToStart.observe(this.firstEl);
            this.isScrolledToEnd.observe(this.lastEl);
          } else {
            this.isScrolledToStart.unobserve(this.firstEl);
            this.isScrolledToEnd.unobserve(this.lastEl);
          }
        });
      }
  
      togglePrevious(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting === true) {
            this.previousButton?.setAttribute('disabled', 'true');
            if (this.nextButton?.hasAttribute('disabled')) {
              this.previousButton?.classList.add('hidden');
              this.sliderBar?.parentElement.classList.add('opacity-0');
            } else {
              this.previousButton?.classList.remove('hidden');
              this.sliderBar?.parentElement.classList.remove('opacity-0');
            }
          } else {
            this.previousButton?.removeAttribute('disabled');
            this.previousButton?.classList.remove('hidden');
            this.sliderBar?.parentElement.classList.remove('opacity-0');
          }
        });
      }
  
      toggleNext(entries) {
        entries.forEach((entry) => {
          if (entry.isIntersecting === true) {
            this.nextButton?.setAttribute('disabled', 'true');
            if (this.previousButton?.hasAttribute('disabled')) {
              this.nextButton?.classList.add('hidden');
              this.sliderBar?.parentElement.classList.add('opacity-0');
            } else {
              this.previousButton?.classList.remove('hidden');
              this.sliderBar?.parentElement.classList.remove('opacity-0');
            }
          } else {
            this.nextButton?.removeAttribute('disabled');
            this.nextButton?.classList.remove('hidden');
            this.sliderBar?.parentElement.classList.remove('opacity-0');
          }
        });
      }
    }
  
    customElements.define('snap-slider', SnapSlider);
  }